<template>
	<main class="teacher-detail-m">
		<header class="teacher-detail-m__header">
			<MHeaderPage :title="t(`${I18N_NS}.teacherProfile`)" />
		</header>
		<PageSkeleton v-if="showPageSkeleton" />
		<TeacherInfo v-else :data="teacherInfo" profile-mode="teacher" />

		<section v-if="!showPageSkeleton" class="teacher-detail-m__content">
			<nav
				v-if="visibleMainTabs.length"
				class="teacher-detail-m__tabs teacher-detail-m__tabs--teacher"
				:aria-label="t(`${I18N_NS}.teacherProfile`)"
			>
				<button
					v-for="tab in visibleMainTabs"
					:key="tab.key"
					type="button"
					class="teacher-detail-m__tab"
					:class="{ 'is-active': activeMainKey === tab.key }"
					@click="switchMainTab(tab.key)"
				>
					{{ tab.label }}
				</button>
			</nav>

			<article
				v-if="isAreaTeacher"
				v-show="activeMainKey === 'services'"
				class="teacher-detail-m__panel"
			>
				<div class="teacher-detail-m__service-tabs">
					<button
						v-if="consultService.length"
						type="button"
						class="teacher-detail-m__service-tab"
						:class="{ 'is-active': activeServiceKey === 'consult' }"
						@click="switchServiceTab('consult')"
					>
						{{ t(`${I18N_NS}.sections.consultService`) }}
					</button>
					<button
						v-if="fahuiService.length"
						type="button"
						class="teacher-detail-m__service-tab"
						:class="{ 'is-active': activeServiceKey === 'solution' }"
						@click="switchServiceTab('solution')"
					>
						{{ t(`${I18N_NS}.sections.solutionService`) }}
					</button>
				</div>

				<ConsultService
					v-if="consultService.length"
					v-show="activeServiceKey === 'consult'"
					:data="consultService"
					:variant="detailVariant"
				/>
				<FahuiService
					v-if="fahuiService.length"
					v-show="activeServiceKey === 'solution'"
					:data="fahuiService"
					:variant="detailVariant"
				/>
			</article>

			<template v-else>
				<article
					v-if="consultService.length"
					v-show="activeMainKey === 'consult'"
					class="teacher-detail-m__panel"
				>
					<ConsultService :data="consultService" :variant="detailVariant" />
				</article>

				<article
					v-if="fahuiService.length"
					v-show="activeMainKey === 'solution'"
					class="teacher-detail-m__panel"
				>
					<FahuiService :data="fahuiService" :variant="detailVariant" />
				</article>
			</template>

			<article
				v-if="hasMediaContent"
				v-show="activeMainKey === 'media'"
				class="teacher-detail-m__panel"
			>
				<MediaContent :data="mediaContent" profile-mode="teacher" />
			</article>

			<article
				v-show="activeMainKey === 'evaluate' && hasEvaluateContent"
				class="teacher-detail-m__panel"
			>
				<ClientOnly>
					<UserEvaluate :teacher-id="teacherId" @has-content-change="hasEvaluateContent = $event" />
				</ClientOnly>
			</article>
		</section>

		<footer v-if="!showPageSkeleton && showChatFooter" class="teacher-detail-m__footer">
			<button type="button" class="teacher-detail-m__chat" @click="toChat">
				{{ chatButtonText }}
			</button>
		</footer>
	</main>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import MHeaderPage from "~/components/m/page-header/index.vue";
import { useI18n } from "~/composables/useI18n";
import ConsultService from "./components/consult-service/index.vue";
import FahuiService from "./components/fahui-service/index.vue";
import MediaContent from "./components/media-content/index.vue";
import TeacherInfo from "./components/teacher-info/index.vue";
import UserEvaluate from "./components/user-evaluate/index.vue";
import PageSkeleton from "./components/page-skeleton/index.vue";
import { useTeacherDetailPageData } from "@variants/seer/teacher-detail/composables/useTeacherDetailPageData";
import { useTeacherDetailPageMode } from "@variants/seer/teacher-detail/composables/useTeacherDetailPageMode";
import {
	provideMentorDetailTracking,
	resolveMentorDetailTabName,
	useMentorDetailTracking,
} from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const route = useRoute();

const teacherId = String(route.params.id || "");
const activeMainKey = ref("consult");
const activeServiceKey = ref("consult");
const hasEvaluateContent = ref(false);
const previousMainTab = ref("");

const { teacherInfo, consultService, fahuiService, mediaContent, pending } =
	useTeacherDetailPageData(teacherId);

const showPageSkeleton = computed(
	() => pending.value && !Object.keys(teacherInfo.value || {}).length
);

const { isAreaTeacher, hasMediaContent } = useTeacherDetailPageMode(
	teacherInfo,
	consultService,
	fahuiService,
	mediaContent
);

const tracking = provideMentorDetailTracking(
	useMentorDetailTracking({
		deviceType: "mobile",
		mentorId: teacherId,
		areaType: computed(() => teacherInfo.value?.area_type),
	})
);
const { track, normalizeMentorRating, getConsultationCountBucket } = tracking;

const pageViewFired = ref(false);
watch(
	teacherInfo,
	info => {
		if (!import.meta.client || pageViewFired.value) return;
		if (!info || !Object.keys(info).length) return;
		pageViewFired.value = true;
		import("~/utils/firebase").then(({ logEvent }) => {
			logEvent("site_masterpage_uv");
		});
		track("mentor_detail_page_view", {
			rating: normalizeMentorRating(info.score),
			consultation_count_bucket: getConsultationCountBucket(info.answers),
		});
	},
	{ immediate: true }
);
const detailVariant = computed(() => (isAreaTeacher.value ? "area" : "default"));
const isEnglish = computed(() => locale.value === "en");
const masterServiceLabel = computed(() => (isEnglish.value ? "Master Service" : "大師服務"));

const visibleMainTabs = computed(() => {
	if (isAreaTeacher.value) {
		return [
			{
				key: "services",
				label: masterServiceLabel.value,
				visible: consultService.value.length > 0 || fahuiService.value.length > 0,
			},
			{
				key: "media",
				label: t(`${I18N_NS}.sections.media`),
				visible: hasMediaContent.value,
			},
			{
				key: "evaluate",
				label: t(`${I18N_NS}.sections.userEvaluate`),
				visible: hasEvaluateContent.value,
			},
		].filter(item => item.visible);
	}

	return [
		{
			key: "consult",
			label: t(`${I18N_NS}.sections.consultService`),
			visible: consultService.value.length > 0,
		},
		{
			key: "solution",
			label: t(`${I18N_NS}.sections.solutionService`),
			visible: fahuiService.value.length > 0,
		},
		{
			key: "media",
			label: t(`${I18N_NS}.sections.media`),
			visible: hasMediaContent.value,
		},
		{
			key: "evaluate",
			label: t(`${I18N_NS}.sections.userEvaluate`),
			visible: hasEvaluateContent.value,
		},
	].filter(item => item.visible);
});

const chatTeacherId = computed(() => {
	if (isAreaTeacher.value || teacherInfo.value?.is_big_teacher) {
		if (isEnabledFlag(teacherInfo.value?.is_assistant)) {
			return normalizeChatId(
				teacherInfo.value?.big_teacher_assistant_str || teacherInfo.value?.big_teacher_assistant
			);
		}
		if (isEnabledFlag(teacherInfo.value?.is_free_chat)) {
			return normalizeChatId(teacherInfo.value?.uid);
		}
		return "";
	}
	return normalizeChatId(teacherInfo.value?.uid);
});

const showChatFooter = computed(() => Boolean(chatTeacherId.value));
const chatButtonText = computed(() =>
	isAreaTeacher.value && isEnabledFlag(teacherInfo.value?.is_assistant)
		? isEnglish.value
			? "Free assistant consultation"
			: "免費咨詢助理"
		: t(`${I18N_NS}.freeConsult`)
);

function isEnabledFlag(value) {
	return value === true || value === 1 || value === "1" || value === "true";
}

function normalizeChatId(value) {
	if (value === undefined || value === null) return "";
	const normalized = String(value).trim();
	return normalized && normalized !== "0" ? normalized : "";
}

watch(
	visibleMainTabs,
	tabs => {
		if (!tabs.length) return;
		if (!tabs.some(item => item.key === activeMainKey.value)) {
			activeMainKey.value = tabs[0].key;
		}
	},
	{ immediate: true }
);

watch(
	[consultService, fahuiService],
	([consult, solution]) => {
		if (activeServiceKey.value === "consult" && !consult.length && solution.length) {
			activeServiceKey.value = "solution";
			return;
		}
		if (activeServiceKey.value === "solution" && !solution.length && consult.length) {
			activeServiceKey.value = "consult";
		}
	},
	{ immediate: true }
);

function switchMainTab(key) {
	if (activeMainKey.value === key) return;
	track("mentor_detail_tab_click", {
		tab_name: resolveMentorDetailTabName(key),
		previous_tab: resolveMentorDetailTabName(previousMainTab.value || activeMainKey.value),
	});
	previousMainTab.value = activeMainKey.value;
	activeMainKey.value = key;
}

function switchServiceTab(key) {
	if (activeServiceKey.value === key) return;
	track("mentor_detail_tab_click", {
		tab_name: resolveMentorDetailTabName(key),
		previous_tab: resolveMentorDetailTabName(activeServiceKey.value),
	});
	activeServiceKey.value = key;
}

const openLogin = inject("openLogin");
const toChat = async () => {
	if (import.meta.client) {
		const { logEvent } = await import("~/utils/firebase");
		logEvent("site_masterpage_ask");
	}
	track("mentor_free_consult_click", {
		cta_position: "footer",
	});
	openLogin?.();
	if (!chatTeacherId.value) return;
	await navigateTo(`/seer/chat/${chatTeacherId.value}`);
};
</script>

<style lang="scss" scoped>
.teacher-detail-m {
	position: fixed;
	inset: 0;
	overflow-y: auto;
	width: 100%;
	padding-bottom: 150px;
	background: #ffffff;
	font-family: "Source Han Serif CN", "PingFang SC", sans-serif;
	-webkit-overflow-scrolling: touch;

	&__header {
		position: sticky;
		top: 0;
		z-index: 20;
		background: #ffffff;

		:deep(.m-page-header) {
			height: 100px;
		}

		:deep(.p-center) {
			font-size: 44px;
			line-height: 100px;
			font-weight: 700;
			color: #333333;
		}

		:deep(.return) {
			left: 24px;
			width: 56px;
			height: 56px;
		}
	}

	&__content {
		margin-top: 32px;
	}

	&__tabs {
		display: flex;
		gap: 48px;
		width: 100%;
		padding: 0 32px;
		overflow-x: auto;
		background: #ffffff;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}

		&--teacher {
			position: sticky;
			top: 100px;
			z-index: 15;
			gap: 44px;
			border-bottom: 1px solid #f3f3f3;
		}
	}

	&__tab {
		position: relative;
		flex: 0 0 auto;
		height: 72px;
		padding: 0;
		border: 0;
		background: transparent;
		color: #999999;
		font-size: 32px;
		font-weight: 400;
		white-space: nowrap;

		&.is-active {
			color: #333333;
			font-weight: 700;

			&::after {
				content: "";
				position: absolute;
				left: 0;
				bottom: 8px;
				width: 62px;
				height: 4px;
				border-radius: 1px;
				background: #333333;
			}
		}
	}

	&__tabs--teacher &__tab {
		height: 80px;
		color: #a0a0a0;
		font-weight: 500;

		&.is-active {
			color: #333333;
			font-weight: 700;

			&::after {
				left: 0;
				right: 0;
				bottom: 0;
				width: auto;
				height: 4px;
				border-radius: 0;
				background: #333333;
			}
		}
	}

	&__panel {
		padding-top: 28px;
	}

	&__service-tabs {
		display: flex;
		gap: 16px;
		padding: 0 32px 24px;
		background: #ffffff;
	}

	&__service-tab {
		min-width: 140px;
		height: 64px;
		padding: 0 24px;
		border: 1px solid transparent;
		border-radius: 8px;
		background: #f6f6f6;
		color: #a0a0a0;
		font-size: 28px;
		line-height: 64px;
		white-space: nowrap;

		&.is-active {
			border-color: #333333;
			background: #ffffff;
			color: #333333;
			font-weight: 500;
		}
	}

	&__footer {
		position: fixed;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 30;
		padding: 18px 32px 24px;
		background: #ffffff;
		box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.06);
	}

	&__chat {
		display: block;
		width: 100%;
		height: 104px;
		border: 0;
		border-radius: 8px;
		background: #333333;
		color: #ffffff;
		font-size: 36px;
		font-weight: 600;
		text-align: center;
		line-height: 104px;
	}
}
</style>
